import {first} from 'lodash';
import React, {FC, useCallback, useEffect, useState} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useLayer} from '../../../../../libs/shared/ui/src/components/layers/layerContext';
import {ActivityModel} from '../../../../models/api/activityModel';
import {useConversationSearch} from '../conversation/highlights/conversationSearchContext';
import {UnreadBannerBase, UnreadBannerPositionsEnum} from './unreadBannerBase';

/*
 * Props.
 */

interface UnreadBannerBelowProps {
  unreadActivitiesBelow: ReadonlyArray<ActivityModel>;
  setReadActivityIds: (computeReadActivityIds: (prevReadActivityIds: Array<string>) => Array<string>) => void;
  onUnreadBannerClick: (arg: number | undefined) => void;
  isInitiallyFocusedOnLessRecentActivity: boolean;
  isAtOrBelowMostRecentActivity: boolean | undefined;
  onJumpToMostRecentClick: () => void;
}

/*
 * Component.
 */

export const UnreadBannerBelow: FC<UnreadBannerBelowProps> = (props) => {
  const {unreadActivitiesBelow, setReadActivityIds, onUnreadBannerClick} = props;
  const [isMostRecentDismissed, setIsMostRecentDismissed] = useState(false);
  const dismissMostRecent = () => setIsMostRecentDismissed(true);

  const hasUnreadActivitiesBelow = unreadActivitiesBelow.length !== 0;
  const [hasUnreadActivitiesBelowDelayed, setHasUnreadActivitiesBelowDelayed] =
    useState(hasUnreadActivitiesBelow);

  const conversationSearchContext = useConversationSearch();

  const onUnreadDismissClick = useCallback(() => {
    setReadActivityIds((prevReadActivityIds) => [
      ...new Set([...prevReadActivityIds, ...unreadActivitiesBelow.map((activity) => `${activity.id}`)]),
    ]);
  }, [setReadActivityIds, unreadActivitiesBelow]);

  const onNavigateToUnread = useCallback(() => {
    const firstNewUnreadActivity = first(unreadActivitiesBelow);
    onUnreadBannerClick(firstNewUnreadActivity?.id);
    onUnreadDismissClick();
  }, [unreadActivitiesBelow, onUnreadBannerClick, onUnreadDismissClick]);

  const onDismissMostRecentClick = dismissMostRecent;

  const onJumpToMostRecentClick = () => {
    props.onJumpToMostRecentClick();
    dismissMostRecent();
  };

  const {window} = useLayer();

  useEffect(() => {
    const handle = window.setInstrumentedTimeout(
      () => setHasUnreadActivitiesBelowDelayed(hasUnreadActivitiesBelow),
      1000,
    );
    return () => window.clearTimeout(handle);
  }, [window, hasUnreadActivitiesBelow]);

  const isCurrentlyInSearchInConversation = conversationSearchContext && conversationSearchContext.query;
  if (isCurrentlyInSearchInConversation) {
    return null;
  }

  // New activities arrived while looking at the conversation.
  if (hasUnreadActivitiesBelow && hasUnreadActivitiesBelowDelayed) {
    return (
      <UnreadBannerBase
        currentPosition={UnreadBannerPositionsEnum.BOTTOM}
        label={<Trans id="unread-banner-below-newunread">{unreadActivitiesBelow.length} unread</Trans>}
        onNavigateToUnreadClick={onNavigateToUnread}
        onDismissBannerClick={onUnreadDismissClick}
      />
    );
  }

  // The most recent activity was initially out of view.
  if (
    props.isInitiallyFocusedOnLessRecentActivity &&
    props.isAtOrBelowMostRecentActivity === false &&
    !isMostRecentDismissed
  ) {
    return (
      <UnreadBannerBase
        currentPosition={UnreadBannerPositionsEnum.BOTTOM}
        label={<Trans id="unread-banner-below-mostrecent">Jump to most recent</Trans>}
        onNavigateToUnreadClick={onJumpToMostRecentClick}
        onDismissBannerClick={onDismissMostRecentClick}
      />
    );
  }

  return null;
};
